import { useEffect, useRef, useState } from 'react'
import { ConfirmAction } from '@/components/confirm-action'
import { FieldError } from '@/components/field-error'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { errorMessage } from '@/lib/utils'
import {
  PLATFORM_LABEL,
  PLATFORMS,
  nativeVersionPolicyInputSchema,
  useNativeVersionPolicy,
  useSaveNativeVersionPolicy,
  useShorebirdReleases,
  useTranslateNativeDraft,
  type NativeVersionPolicy,
  type NativeVersionPolicyInput,
  type Platform,
  type Reasons,
} from '@/features/app-patch-policy/api'
import { useLocale } from '@/features/app-patch-policy/locale'
import { NativeMessageTranslationsDialog } from '@/features/app-patch-policy/native-message-translations-dialog'

const DEFAULT_REASONS: Reasons = { security: true, performance: true, feature: true }

function reasonsEqual(a: Reasons, b: Reasons): boolean {
  return a.security === b.security && a.performance === b.performance && a.feature === b.feature
}

/**
 * Section B: the native app-store-level forced-update gate — independent of Section A's Shorebird
 * patch floor, for gaps a code-push patch can never fix. One version/title/message/reasons input
 * always saves as the recommended (optional) threshold; "Also set as mandatory" additionally
 * enforces the same values as the hard floor — same one-input + set_mandatory-checkbox shape as
 * Section A's Promote panel, rather than two parallel forms for what's usually the same edit.
 */
export function NativeVersionCard() {
  const { t } = useLocale()

  const iosPolicy = useNativeVersionPolicy('ios')
  const androidPolicy = useNativeVersionPolicy('android')
  const policyByPlatform: Record<Platform, typeof iosPolicy> = { ios: iosPolicy, android: androidPolicy }

  const saveIos = useSaveNativeVersionPolicy('ios')
  const saveAndroid = useSaveNativeVersionPolicy('android')
  const saveByPlatform: Record<Platform, typeof saveIos> = { ios: saveIos, android: saveAndroid }

  // Shorebird's release_version (e.g. "1.0.1+2") is exactly a platform's native version+build
  // combined — every release a platform has ever had (not just what's on stable) is a reasonable
  // source to pick a known version+build pair from.
  const iosReleases = useShorebirdReleases('ios')
  const androidReleases = useShorebirdReleases('android')
  const releasesByPlatform: Record<Platform, typeof iosReleases> = { ios: iosReleases, android: androidReleases }

  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>([])
  const [minVersion, setMinVersion] = useState('')
  const [minBuildNumber, setMinBuildNumber] = useState<number | null>(null)
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [reasons, setReasons] = useState<Reasons>(DEFAULT_REASONS)
  // Mirrors Section A's set_mandatory: unchecked, this save only touches the optional/recommended
  // threshold; checked, the same version/title/message/reasons also become the hard floor.
  const [alsoMandatory, setAlsoMandatory] = useState(false)
  const [outcomes, setOutcomes] = useState<{ platform: Platform; skipped?: boolean; error?: string }[] | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [pendingClear, setPendingClear] = useState<Platform | null>(null)
  const [translationsOpen, setTranslationsOpen] = useState(false)

  const browsePlatform = selectedPlatforms[0] ?? null
  // A device below the mandatory floor never even reaches the optional check (see
  // ShorebirdUpdateService.checkForUpdates), so only the variant currently in force needs its
  // translations kept current — the other one's copy is unreachable while alsoMandatory holds.
  const activeVariant = alsoMandatory ? 'mandatory' : 'optional'
  const translateDraft = useTranslateNativeDraft(browsePlatform ?? 'ios', activeVariant)
  const releasesQuery = browsePlatform ? releasesByPlatform[browsePlatform] : iosReleases

  // Prefills once per platform from its current gate: the save is a full overwrite, so a blank
  // field here would null out the existing value. Mandatory wins the prefill when both are set —
  // it's the stricter, currently-enforced one.
  const prefilledPlatformRef = useRef<Platform | null>(null)
  useEffect(() => {
    if (!browsePlatform) return
    const policy = policyByPlatform[browsePlatform]
    if (!policy.isSuccess) return
    if (prefilledPlatformRef.current === browsePlatform) return
    prefilledPlatformRef.current = browsePlatform
    const data = policy.data
    const mandatoryIsSet = data?.min_version != null
    setMinVersion((mandatoryIsSet ? data?.min_version : data?.optional_min_version) ?? '')
    setMinBuildNumber((mandatoryIsSet ? data?.min_build_number : data?.optional_min_build_number) ?? null)
    setTitle((mandatoryIsSet ? data?.title : data?.optional_title) ?? '')
    setMessage((mandatoryIsSet ? data?.message : data?.optional_message) ?? '')
    setReasons((mandatoryIsSet ? data?.mandatory_reasons : data?.optional_reasons) ?? DEFAULT_REASONS)
    setAlsoMandatory(mandatoryIsSet)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browsePlatform, browsePlatform ? policyByPlatform[browsePlatform].isSuccess : null, browsePlatform ? policyByPlatform[browsePlatform].data : null])

  function togglePlatform(platform: Platform, checked: boolean) {
    setSelectedPlatforms((prev) => {
      const next = checked ? [...prev, platform] : prev.filter((p) => p !== platform)
      // No platform browsed means nothing is prefilled — clearing the ref lets a later re-select
      // of the same platform prefill again instead of staying blocked by a stale key.
      if (next.length === 0) prefilledPlatformRef.current = null
      return next
    })
  }

  function pickRelease(releaseId: number) {
    const release = releasesQuery.data?.find((r) => r.id === releaseId)
    if (!release) return
    const [version, build] = release.version.split('+')
    const buildNumber = Number(build)
    setMinVersion(version)
    setMinBuildNumber(build !== undefined && Number.isFinite(buildNumber) ? buildNumber : null)
  }

  // The optional/recommended threshold always reflects the form; the mandatory floor only moves
  // to match it when alsoMandatory is checked, otherwise it's carried forward untouched from
  // whatever that platform's row currently has — same "don't touch what you didn't check" contract
  // as Section A's set_mandatory.
  function buildInputFor(platform: Platform): NativeVersionPolicyInput {
    const existing = policyByPlatform[platform].data
    const trimmedVersion = minVersion.trim() === '' ? null : minVersion.trim()
    const trimmedTitle = title.trim() === '' ? null : title
    const trimmedMessage = message.trim() === '' ? null : message
    return {
      min_version: alsoMandatory ? trimmedVersion : (existing?.min_version ?? null),
      min_build_number: alsoMandatory ? minBuildNumber : (existing?.min_build_number ?? null),
      title: alsoMandatory ? trimmedTitle : (existing?.title ?? null),
      message: alsoMandatory ? trimmedMessage : (existing?.message ?? null),
      mandatory_reasons: alsoMandatory ? reasons : (existing?.mandatory_reasons ?? DEFAULT_REASONS),
      optional_min_version: trimmedVersion,
      optional_min_build_number: minBuildNumber,
      optional_title: trimmedTitle,
      optional_message: trimmedMessage,
      optional_reasons: reasons,
    }
  }

  const previewInput = buildInputFor(browsePlatform ?? 'ios')
  const parsedInput = nativeVersionPolicyInputSchema.safeParse(previewInput)
  const validInput = parsedInput.success
  // Surfaced next to the build-number field below — safeParse's boolean alone gives the operator
  // no reason for a Save button that's inexplicably disabled.
  const buildNumberError = parsedInput.success
    ? undefined
    : parsedInput.error.issues.find((issue) => issue.path[0] === 'optional_min_build_number')?.message

  function inputsEqual(existing: NativeVersionPolicy | undefined, next: NativeVersionPolicyInput): boolean {
    if (!existing) return false
    return (
      existing.min_version === next.min_version &&
      existing.min_build_number === next.min_build_number &&
      existing.message === next.message &&
      existing.title === next.title &&
      reasonsEqual(existing.mandatory_reasons, next.mandatory_reasons) &&
      existing.optional_min_version === next.optional_min_version &&
      existing.optional_min_build_number === next.optional_min_build_number &&
      existing.optional_title === next.optional_title &&
      existing.optional_message === next.optional_message &&
      reasonsEqual(existing.optional_reasons, next.optional_reasons)
    )
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault()
    setOutcomes(null)
    setSubmitting(true)
    const results = await Promise.all(
      selectedPlatforms.map(async (platform) => {
        const input = buildInputFor(platform)
        if (inputsEqual(policyByPlatform[platform].data, input)) {
          return { platform, skipped: true }
        }
        try {
          await saveByPlatform[platform].mutateAsync(input)
          return { platform }
        } catch (err) {
          return { platform, error: errorMessage(err, 'Failed to save.') }
        }
      }),
    )
    setSubmitting(false)
    setOutcomes(results)
    if (!results.some((r) => r.error)) {
      setSelectedPlatforms([])
      setMinVersion('')
      setMinBuildNumber(null)
      setTitle('')
      setMessage('')
      setReasons(DEFAULT_REASONS)
      setAlsoMandatory(false)
      // Without this, re-selecting the same platform after a successful save is blocked from
      // prefilling again — its ref key never changed — and shows stale/blank fields instead.
      prefilledPlatformRef.current = null
    }
  }

  const policiesLoaded = iosPolicy.isSuccess && androidPolicy.isSuccess

  return (
    <div className="space-y-3">
      <Label>B · {t('nativeVersion.heading')}</Label>
      <p className="text-xs text-faint">{t('nativeVersion.help')}</p>

      {(iosPolicy.isPending || androidPolicy.isPending) && <Skeleton className="h-9 w-full rounded-md" />}
      {(iosPolicy.isError || androidPolicy.isError) && (
        <p className="text-xs text-destructive">{t('nativeVersion.loadError')}</p>
      )}
      {policiesLoaded && (
        <ul className="space-y-1.5">
          {PLATFORMS.map((platform) => {
            const policy = policyByPlatform[platform].data
            return (
              <li
                key={platform}
                className="flex flex-col gap-1 rounded-md border border-border px-3 py-2 text-sm"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{PLATFORM_LABEL[platform]}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-muted-foreground">
                      {policy?.min_version
                        ? t('nativeVersion.onSummary', {
                            version: policy.min_version + (policy.min_build_number != null ? `+${policy.min_build_number}` : ''),
                          })
                        : t('nativeVersion.offShort')}
                    </span>
                    {policy?.min_version && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={saveByPlatform[platform].isPending}
                        onClick={() => setPendingClear(platform)}
                      >
                        {t('common.clear')}
                      </Button>
                    )}
                  </span>
                </div>
                {policy?.optional_min_version && (
                  <div className="flex items-center justify-between gap-2">
                    <span />
                    <span className="flex items-center gap-2">
                      <span className="text-muted-foreground">
                        {t('nativeVersion.optionalOnSummary', {
                          version:
                            policy.optional_min_version +
                            (policy.optional_min_build_number != null ? `+${policy.optional_min_build_number}` : ''),
                        })}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={saveByPlatform[platform].isPending}
                        onClick={() =>
                          saveByPlatform[platform].mutateAsync({
                            min_version: policy.min_version,
                            min_build_number: policy.min_build_number,
                            message: policy.message,
                            title: policy.title,
                            mandatory_reasons: policy.mandatory_reasons,
                            optional_min_version: null,
                            optional_min_build_number: null,
                            optional_title: policy.optional_title,
                            optional_message: policy.optional_message,
                            optional_reasons: policy.optional_reasons,
                          })
                        }
                      >
                        {t('common.clear')}
                      </Button>
                    </span>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      <form className="space-y-3 rounded-md border border-border p-3" onSubmit={onSubmit}>
        <div className="space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <Label>{t('rules.platformLabel')}</Label>
            <span className="text-[11px] text-faint">{t('rules.multiSelectHint')}</span>
          </div>
          <div className="flex flex-col gap-2">
            {PLATFORMS.map((p) => (
              <label key={p} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={selectedPlatforms.includes(p)}
                  onCheckedChange={(value) => togglePlatform(p, Boolean(value))}
                />
                {PLATFORM_LABEL[p]}
              </label>
            ))}
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="native-release-picker" className="text-xs">
            {t('nativeVersion.pickFromReleases')}
          </Label>
          {browsePlatform ? (
            <Select
              onValueChange={(value) => pickRelease(Number(value))}
              disabled={releasesQuery.isPending || releasesQuery.isError}
            >
              <SelectTrigger id="native-release-picker" className="w-56">
                <SelectValue
                  placeholder={
                    releasesQuery.isPending
                      ? t('promote.pickerLoadingReleases')
                      : releasesQuery.data?.length === 0
                        ? t('nativeVersion.pickerEmpty')
                        : t('nativeVersion.pickerPrompt')
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {releasesQuery.data?.map((release) => (
                  <SelectItem key={release.id} value={String(release.id)}>
                    {release.version}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className="text-xs text-muted-foreground">{t('rules.checkPlatformFirst')}</p>
          )}
          {releasesQuery.isError && <p className="text-xs text-warning">{t('promote.releasesLoadError')}</p>}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="native-min-version" className="text-xs">
              {t('nativeVersion.minVersionLabel')}
            </Label>
            <Input
              id="native-min-version"
              placeholder={t('nativeVersion.minVersionPlaceholder')}
              value={minVersion}
              onChange={(event) => setMinVersion(event.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="native-min-build" className="text-xs">
              {t('nativeVersion.minBuildLabel')}
            </Label>
            <Input
              id="native-min-build"
              type="number"
              min={0}
              step={1}
              placeholder={t('nativeVersion.minBuildPlaceholder')}
              value={minBuildNumber ?? ''}
              onChange={(event) => setMinBuildNumber(event.target.value === '' ? null : Number(event.target.value))}
            />
            <FieldError message={buildNumberError} />
            <p className="text-xs text-faint">{t('nativeVersion.minBuildHelper')}</p>
          </div>
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="native-title" className="text-xs">
              {t('nativeVersion.titleLabel')}
            </Label>
            <Button type="button" variant="ghost" size="sm" disabled={!browsePlatform} onClick={() => setTranslationsOpen(true)}>
              {t('nativeVersion.translations')}
            </Button>
          </div>
          <Input
            id="native-title"
            maxLength={64}
            placeholder={t('nativeVersion.titlePlaceholder')}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            onBlur={() => {
              if (!browsePlatform) return
              if (message.trim()) translateDraft.mutate({ title: title.trim(), message: message.trim() })
            }}
          />
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="native-message" className="text-xs">
              {t('nativeVersion.messageLabel')}
            </Label>
            <Button type="button" variant="ghost" size="sm" disabled={!browsePlatform} onClick={() => setTranslationsOpen(true)}>
              {t('nativeVersion.translations')}
            </Button>
          </div>
          <Textarea
            id="native-message"
            rows={2}
            placeholder={t('nativeVersion.messagePlaceholder')}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            onBlur={(event) => {
              if (!browsePlatform) return
              const value = event.target.value.trim()
              if (value) translateDraft.mutate({ title: title.trim(), message: value })
            }}
          />
        </div>

        <ReasonsField
          label={t('nativeVersion.reasonsLabel')}
          value={reasons}
          onChange={setReasons}
          securityLabel={t('nativeVersion.reasonSecurity')}
          performanceLabel={t('nativeVersion.reasonPerformance')}
          featureLabel={t('nativeVersion.reasonFeature')}
        />

        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={alsoMandatory} onCheckedChange={(value) => setAlsoMandatory(Boolean(value))} />
          {t('nativeVersion.alsoMandatory')}
        </label>
        <p className="text-xs text-faint">{t('nativeVersion.alsoMandatoryHelp')}</p>

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" size="sm" disabled={selectedPlatforms.length === 0 || !validInput || submitting}>
            {submitting ? t('common.saving') : t('common.save')}
          </Button>
        </div>

        {outcomes && (
          <div className="space-y-0.5">
            {outcomes.map((outcome) => (
              <p
                key={outcome.platform}
                className={`text-xs ${outcome.error ? 'text-destructive' : 'text-muted-foreground'}`}
              >
                {PLATFORM_LABEL[outcome.platform]}:{' '}
                {outcome.error ? outcome.error : outcome.skipped ? t('rules.unchanged') : t('rules.saved')}
              </p>
            ))}
          </div>
        )}
      </form>

      <ConfirmAction
        open={pendingClear !== null}
        onOpenChange={(open) => !open && setPendingClear(null)}
        title="Clear this native version gate?"
        description={
          pendingClear && (
            <>
              Turns off the {PLATFORM_LABEL[pendingClear]} native-version gate entirely. Devices below the current
              minimum will no longer be blocked and sent to the store.
            </>
          )
        }
        confirmLabel="Clear Gate"
        destructive
        onConfirm={() => {
          // A full overwrite (see SaveNativeVersionPolicy), so this must carry every other field
          // forward unchanged — only the mandatory floor itself is being cleared here.
          const existing = policyByPlatform[pendingClear!].data
          return saveByPlatform[pendingClear!].mutateAsync({
            min_version: null,
            min_build_number: null,
            message: existing?.message ?? null,
            title: existing?.title ?? null,
            mandatory_reasons: existing?.mandatory_reasons ?? DEFAULT_REASONS,
            optional_min_version: existing?.optional_min_version ?? null,
            optional_min_build_number: existing?.optional_min_build_number ?? null,
            optional_title: existing?.optional_title ?? null,
            optional_message: existing?.optional_message ?? null,
            optional_reasons: existing?.optional_reasons ?? DEFAULT_REASONS,
          })
        }}
      />

      {browsePlatform && translationsOpen && (
        <NativeMessageTranslationsDialog
          platform={browsePlatform}
          variant={activeVariant}
          englishTitle={title}
          englishMessage={message}
          open
          onOpenChange={(open) => !open && setTranslationsOpen(false)}
        />
      )}
    </div>
  )
}

function ReasonsField({
  label,
  value,
  onChange,
  securityLabel,
  performanceLabel,
  featureLabel,
}: {
  label: string
  value: Reasons
  onChange: (next: Reasons) => void
  securityLabel: string
  performanceLabel: string
  featureLabel: string
}) {
  const options: { key: keyof Reasons; label: string }[] = [
    { key: 'security', label: securityLabel },
    { key: 'performance', label: performanceLabel },
    { key: 'feature', label: featureLabel },
  ]
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <div className="flex flex-col gap-2">
        {options.map((option) => (
          <label key={option.key} className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={value[option.key]}
              onCheckedChange={(checked) => onChange({ ...value, [option.key]: Boolean(checked) })}
            />
            {option.label}
          </label>
        ))}
      </div>
    </div>
  )
}
